import type { Question, ValidationResult } from './types';

const ok = (message = ''): ValidationResult => ({ valid: true, message });
const fail = (message: string): ValidationResult => ({ valid: false, message });

/**
 * US phone validation against NANP rules:
 *   - exactly 10 digits
 *   - area code does not start with 0 or 1, is not 555, is not an N11 service code
 *   - exchange does not start with 0 or 1
 *   - rejects all-same-digit and obvious sequential placeholders
 *   - rejects 555-01XX (reserved fictional)
 */
export function validateUSPhone(raw: string): ValidationResult {
  const digits = raw.replace(/\D/g, '');

  if (digits.length === 0) return fail('');
  if (digits.length < 10) return fail(`Please enter a complete 10-digit phone number (${digits.length}/10)`);
  if (digits.length > 10) return fail('Phone number must be exactly 10 digits');

  if (/^(\d)\1+$/.test(digits)) return fail('Please enter a real phone number');
  if (digits === '1234567890' || digits === '0123456789' || digits === '9876543210') {
    return fail('Please enter a real phone number');
  }

  const areaCode = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const subscriber = digits.slice(6, 10);

  if (areaCode[0] === '0' || areaCode[0] === '1') {
    return fail('Invalid area code — cannot start with 0 or 1');
  }
  if (areaCode === '555') {
    return fail('Please enter a real phone number (555 area code is reserved)');
  }
  if (/^[2-9]11$/.test(areaCode)) return fail('Invalid area code');

  if (exchange[0] === '0' || exchange[0] === '1') {
    return fail('Invalid phone number — exchange cannot start with 0 or 1');
  }
  if (exchange === '555' && /^01\d\d$/.test(subscriber)) {
    return fail('Please enter a real phone number');
  }

  return ok('✓ Valid phone number');
}

export function formatPhoneAsTyping(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const COMMON_EMAIL_TYPOS = new Set([
  'gmial.com',
  'gmal.com',
  'gmai.com',
  'yhoo.com',
  'yaho.com',
  'hotnail.com',
  'hotmial.com',
  'outlok.com',
]);

export function validateEmail(value: string): ValidationResult {
  if (!value) return fail('');
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!re.test(value)) return fail('Please enter a valid email address');
  const domain = value.split('@')[1]?.toLowerCase() ?? '';
  if (COMMON_EMAIL_TYPOS.has(domain)) return fail('Did you mean a different domain? Please double-check');
  return ok('✓ Looks good');
}

/** Full name = at least two whitespace-separated tokens, letters/diacritics/-/'/. allowed. */
export function validateFullName(value: string): ValidationResult {
  if (!value) return fail('');
  const trimmed = value.trim();
  if (trimmed.length < 2) return fail('Full name must be at least 2 characters');
  if (!/^[a-zA-ZÀ-ſ\s\-'.]+$/.test(trimmed)) return fail('Name can only contain letters');
  const parts = trimmed.split(/\s+/);
  if (parts.length < 2 || parts.some(p => p.length < 1)) {
    return fail('Please enter your first and last name');
  }
  return ok('✓ Looks good');
}

export function validateAge(value: string, min: number, max: number): ValidationResult {
  if (!value) return fail('');
  if (!/^\d+$/.test(value)) return fail('Please enter a whole number');
  const n = Number(value);
  if (n < min) return fail(`You must be at least ${min} years old`);
  if (n > max) return fail(`Please enter an age under ${max}`);
  return ok('✓ Looks good');
}

export function validateRequired(value: string): ValidationResult {
  if (!value || !value.trim()) return fail('Please make a selection');
  return ok();
}

/**
 * Dispatches per question type. Returns { valid, message } for inline status display.
 * For empty values on required fields, returns invalid with empty message — UI uses this
 * to disable the next button silently rather than yelling at users who haven't typed yet.
 */
export function validateQuestion(question: Question, value: string): ValidationResult {
  switch (question.type) {
    case 'single_select':
    case 'boolean':
    case 'dropdown':
      return validateRequired(value);
    case 'text':
      return validateFullName(value);
    case 'email':
      return validateEmail(value);
    case 'number':
      return validateAge(value, question.validation.min, question.validation.max);
    case 'tel':
      return validateUSPhone(value);
  }
}
